import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';

import {
  capitalizeString,
  convertNumToCurrencyStr,
  formatDate,
} from '../../../utils/globalHelpers';
import { getNextPaymentDueDetails } from '../../Main/selectors';
import { NO_TRANSACTIONS_MESSAGE } from './constants';
import {
  formatPaymentTransactionType,
  generateGroupedTransactions,
  parseHomeTransactions,
} from './helpers';
import { initialState } from './reducer';

/**
 * Direct selector to the Payment History state domain
 */
const selectPaymentHistoryDomain = (state) => state.paymentHistory || initialState;

/**
 * Direct selector to the intl object
 */
const selectIntl = (_, props) => props.intl;

/**
 * @description selects target error via prop
 */
const makeSelectError = (prop) => createSelector(
  makeSelectPaymentHistory('errors'),
  (errorState) => errorState[prop],
);

/**
 * @description selects/formats transactions for FlatList component
 */
const makeSelectFlatListTransactions = () => createSelector(
  makeSelectPaymentHistory('loading'),
  makeSelectPaymentHistory('transactions'),
  selectIntl,
  (loading, transactions, intl) => {
    const formattedTransactions = generateGroupedTransactions(transactions.map(({
      transactionCategory,
      transactionDate,
      transactionDescription,
      transactionId,
      transactionTotalAmount,
    }) => ({
      amount: convertNumToCurrencyStr(transactionTotalAmount),
      category: transactionCategory,
      date: formatDate(transactionDate, 'UTC', 'll'),
      id: `${transactionId}`,
      isSkeletonData: false,
      type: formatPaymentTransactionType({ intl, transactionDescription }),
    })));

    const skeletonTransactions =
      new Array(5).fill(null).map((v, i) => ([{
        amount: '',
        date: '',
        id: `${i}`,
        isSkeletonData: true,
        type: '',
      }]));

    return loading.transactions ?
      formattedTransactions.concat(skeletonTransactions) :
      formattedTransactions;
  },
);

/**
 * @description selects target error via prop
 */
const makeSelectLoading = (prop) => createSelector(
  makeSelectPaymentHistory('loading'),
  (loadingState) => loadingState[prop],
);

/**
 * @description Selects errors and formatted form input errors for screen reader
 */
const makeSelectPaymentHistoryNotifications = () => createSelector(
  makeSelectError('serviceError'),
  makeSelectPaymentHistory('totalPages'),
  makeSelectPaymentHistory('transactions'),
  (
    {
      transactionDetail: transactionDetailError,
      transactions: transactionsError,
    },
    totalPages,
    transactions,
  ) => {
    const notifications = [];
    if (transactionsError || transactionDetailError) {
      if (transactionsError) {
        notifications.push(transactionsError);
      }
      if (transactionDetailError) {
        notifications.push(transactionDetailError);
      }
    } else if (totalPages !== -1 && !transactions.length) {
      notifications.push(NO_TRANSACTIONS_MESSAGE);
    }

    return notifications;
  },
);

/**
 * @description Selects/formats transaction detail
 */
const makeSelectTransactionDetails = () => createSelector(
  makeSelectPaymentHistory('openTransaction'),
  selectIntl,
  (transaction, intl) => {
    if (!isEmpty(transaction)) {
      const {
        aHInsuranceAmount,
        escrowBalance,
        feeAmount,
        lifeInsuranceAmount,
        miscellaneousAmount,
        paymentEscrowAmount,
        paymentInterestAmount,
        paymentPrincipalAmount,
        principalBalance,
        replacementResAmount,
        restrictedEscrowAmount,
        segmentNumber,
        suspenseAmount,
        transactionDate,
        transactionDescription,
        transactionTotalAmount,
      } = transaction;
      const fee = feeAmount || 0;

      return ({
        aHInsuranceAmount: convertNumToCurrencyStr(aHInsuranceAmount),
        escrowBalance: convertNumToCurrencyStr(escrowBalance),
        feeAmount: convertNumToCurrencyStr(fee),
        lifeInsuranceAmount: convertNumToCurrencyStr(lifeInsuranceAmount),
        miscellaneousAmount: convertNumToCurrencyStr(miscellaneousAmount),
        monthlyPayment: convertNumToCurrencyStr(paymentInterestAmount + paymentPrincipalAmount),
        paymentEscrowAmount: convertNumToCurrencyStr(paymentEscrowAmount),
        paymentInstallmentAmount:
          convertNumToCurrencyStr(paymentInterestAmount + paymentPrincipalAmount),
        paymentInterestAmount: convertNumToCurrencyStr(paymentInterestAmount),
        paymentPrincipalAmount: convertNumToCurrencyStr(paymentPrincipalAmount),
        principalBalance: convertNumToCurrencyStr(principalBalance),
        replacementResAmount: convertNumToCurrencyStr(replacementResAmount),
        restrictedEscrowAmount: convertNumToCurrencyStr(restrictedEscrowAmount),
        segmentNumber,
        suspenseAmount: convertNumToCurrencyStr(suspenseAmount),
        transactionDate: formatDate(transactionDate, 'UTC', 'LL'),
        transactionTotalAmount: convertNumToCurrencyStr(transactionTotalAmount),
        type: formatPaymentTransactionType({ intl, transactionDescription }),
      });
    }
    return {};
  },
);

/**
 * @description selects/formats transactions for list component
 */
const makeSelectTransactions = () => createSelector(
  makeSelectPaymentHistory('transactions'),
  selectIntl,
  // eslint-disable-next-line arrow-body-style
  (transactions, intl) => generateGroupedTransactions(transactions.map(({
    transactionCategory,
    transactionDate,
    transactionDescription,
    transactionId,
    transactionTotalAmount,
  }) => ({
    amount: convertNumToCurrencyStr(transactionTotalAmount),
    category: transactionCategory,
    date: formatDate(transactionDate, 'UTC', 'll'),
    id: `${transactionId}`,
    isSkeletonData: false,
    type: formatPaymentTransactionType({ intl, transactionDescription }),
  }))),
);

/**
 * selectDrawerHeaderDate
 * @description selects next payment due date for payment history
 *              detail drawer.
 */
const selectDrawerHeaderDate = () => createSelector(
  getNextPaymentDueDetails(),
  (nextPaymentDetails) => {
    const { nextPaymentDueDate } = nextPaymentDetails || {};
    return nextPaymentDueDate
      ? `Due ${formatDate(nextPaymentDueDate, 'UTC', 'MMMM D, YYYY')}`
      : 'No Payment Due';
  },
);

/**
 * @description selects and formats transactions to display first 3
 * grouped transactions for the payment history card in Home screen.
 */
const selectPaymentHomeHistoryData = () => createSelector(
  makeSelectPaymentHistory('transactions'),
  (transactions) => parseHomeTransactions(
    generateGroupedTransactions(transactions.map(({
      transactionCategory,
      transactionDate,
      transactionDescription,
      transactionId,
      transactionTotalAmount,
    }) => ({
      amount: convertNumToCurrencyStr(transactionTotalAmount),
      category: transactionCategory,
      date: formatDate(transactionDate, 'UTC', 'll'),
      id: Number(transactionId),
      isSkeletonData: false,
      type: capitalizeString(transactionDescription),
    }))),
  ),
);

/**
 * Default selector used by Payment History
 */
const makeSelectPaymentHistory = (prop) => createSelector(
  selectPaymentHistoryDomain,
  (state) => state[prop],
);

export default makeSelectPaymentHistory;
export {
  makeSelectError,
  makeSelectFlatListTransactions,
  makeSelectLoading,
  makeSelectPaymentHistoryNotifications,
  makeSelectTransactionDetails,
  makeSelectTransactions,
  selectDrawerHeaderDate,
  selectPaymentHomeHistoryData,
};
